import { SCHEMA_VERSION } from "./constants";
import { newNodeId } from "./ids";
import { defaultPropsFor, type ComponentType } from "./component-rules";
import type { Theme } from "./types.gen";

export interface SiteNode {
  id: string;
  type: ComponentType;
  props: Record<string, unknown>;
  responsive?: {
    tablet?: Record<string, unknown>;
    mobile?: Record<string, unknown>;
  };
  children?: SiteNode[];
}

export interface Page {
  id: string;
  slug: string;
  title: string;
  navLabel?: string;
  showInNav?: boolean;
  seo?: { title?: string; description?: string };
  nodes: SiteNode[];
}

export interface SiteDocument {
  schemaVersion: number;
  meta?: {
    title?: string;
    description?: string;
    faviconAssetId?: string | null;
    ogImageAssetId?: string | null;
    robots?: "index" | "noindex";
  };
  theme: Theme;
  header?: SiteNode;
  footer?: SiteNode;
  pages: Page[];
}

export function createNode(
  type: ComponentType,
  overrides?: Partial<SiteNode>,
): SiteNode {
  return {
    id: newNodeId(),
    type,
    props: defaultPropsFor(type),
    ...overrides,
  };
}

export function createPage(title: string, slug?: string): Page {
  const pageSlug =
    slug ??
    "/" +
      title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
  return {
    id: newNodeId(),
    slug: pageSlug,
    title,
    navLabel: title,
    showInNav: true,
    nodes: [],
  };
}

/** The minimal valid document used when creating a site (PRD §175). */
export function createEmptyDocument(siteName: string): SiteDocument {
  const home = createPage("Home", "/");
  home.nodes = [
    {
      ...createNode("Section"),
      children: [createNode("Heading"), createNode("Text")],
    },
  ];
  return {
    schemaVersion: SCHEMA_VERSION,
    meta: { title: siteName, description: "", robots: "index" },
    theme: {
      colors: {
        primary: "#4f46e5",
        secondary: "#0ea5e9",
        accent: "#f59e0b",
        background: "#ffffff",
        surface: "#f8fafc",
        text: "#0f172a",
        muted: "#64748b",
        border: "#e2e8f0",
      },
      typography: { headingFont: "inter", bodyFont: "inter" },
      radius: "md",
    },
    pages: [home],
  };
}
